import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { Phone, Map } from "lucide-react";

export default function FaskesStroke() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("faskes");
  const [facilities, setFacilities] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [search, setSearch] = useState("");
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation tidak didukung oleh browser Anda.");
      setLoading(false);
      console.error("Geolocation not supported by browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        console.log(
          "User location:",
          position.coords.latitude,
          position.coords.longitude
        );
      },
      (error) => {
        setLocationError(
          "Gagal mendapatkan lokasi. Izinkan akses lokasi. " + error.message
        );
        setLoading(false);
      }
    );
  }, []);

  // Di FaskesStroke.js, sekitar line 34-65
  useEffect(() => {
    const fetchFaskesAndDoctors = async () => {
      setLoading(true);
      // Fetch Faskes
      try {
        let faskesData = [];

        const faskesRes = await api.get("/faskes/location", {
          params: { lat: userLocation.lat, lng: userLocation.lng },
        });

        console.log("Full faskes response:", faskesRes.data);

        // Handle different response structures
        if (Array.isArray(faskesRes.data)) {
          faskesData = faskesRes.data;
        } else if (faskesRes.data && Array.isArray(faskesRes.data.results)) {
          faskesData = faskesRes.data.results;
        } else if (faskesRes.data && faskesRes.data.data) {
          faskesData = faskesRes.data.data;
        } else {
          console.warn("Unexpected faskes response structure:", faskesRes.data);
          faskesData = [];
        }

        // Fallback: jika tidak ada hasil dari /location, coba /nearby
        if (faskesData.length === 0) {
          console.log("No results from /location, trying /nearby...");
          try {
            const nearbyRes = await api.get("/faskes/nearby", {
              params: {
                lat: userLocation.lat,
                lng: userLocation.lng,
                radius: 5000, // 50km radius
              },
            });

            if (nearbyRes.data && nearbyRes.data.results) {
              faskesData = nearbyRes.data.results;
              console.log("Found results from /nearby:", faskesData.length);
            }
          } catch (nearbyErr) {
            console.error("Error fetching nearby faskes:", nearbyErr);
          }
        }

        // Transform data
        faskesData.sort(
          (a, b) =>
            (a.distance_km || a.distance || 0) -
            (b.distance_km || b.distance || 0)
        );
        setFacilities(
          faskesData.map((item) => ({
            name: item.name || item.Name || item.Nama || "Nama tidak tersedia",
            address:
              item.vicinity ||
              item.Alamat ||
              item.address ||
              "Alamat tidak tersedia",
            distance: item.distance_km
              ? `${item.distance_km.toFixed(2)} km`
              : item.distance
                ? `${item.distance.toFixed(2)} km`
                : "",
            phone: item.formatted_phone_number || item.NomorTelepon || "",
            maps_url:
              item.maps_url ||
              item.Maps_url ||
              item.GoogleMapsURL ||
              `https://www.google.com/maps/search/?api=1&query=${item.geometry?.location?.lat || item.lat || 0},${item.geometry?.location?.lng || item.lng || 0}`,
          }))
        );
      } catch (err) {
        console.error("Error fetching faskes:", err);
        setFacilities([]);

        // Tambahkan pesan error yang lebih informatif
        if (err.response?.status === 404) {
          console.log("No faskes found in this location");
        } else if (err.response?.status === 400) {
          console.error("Invalid location parameters");
        }
        setLocationError("Gagal memuat faskes. Pastikan lokasi Anda benar.");
      }

      // Fetch Dokter data (unchanged)
      try {
        const dokterRes = await api.get("/dokter");
        const dokterData = dokterRes.data || [];
        setDoctors(
          dokterData.map((doc) => ({
            id: doc.ID || doc.id,
            name: doc.nama,
            schedule: (doc.jadwal_praktik || []).map(
              (j) =>
                `${j.hari}, ${j.jam_mulai?.slice(0, 5) || ""} - ${
                  j.jam_selesai?.slice(0, 5) || ""
                }`
            ),
            facility:
              doc.faskes && doc.faskes.length > 0
                ? doc.faskes.map((f) => f.Nama || f.name).join(", ")
                : "-",
            distance: "",
            consultationUrl: doc.consultation_url || "",
            location:
              doc.faskes && doc.faskes.length > 0 && doc.faskes[0]
                ? {
                    lat: doc.faskes[0].lat || doc.faskes[0].Lat,
                    lng: doc.faskes[0].lng || doc.faskes[0].Lng,
                  }
                : null,
          }))
        );
      } catch (err) {
        console.error("Error fetching doctors:", err);
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    if (userLocation) {
      fetchFaskesAndDoctors();
    }
    // Log user activity
    try {
      if (userLoading) return;
      logUserActivity(
        "user",
        "view_faskes_doctors",
        "FaskesStroke",
        `User viewed facilities and doctors near location: ${userLocation.lat}, ${userLocation.lng}`
      );
    } catch (err) {
      logUserActivity(
        "user",
        "view_faskes_doctors",
        "FaskesStroke",
        "User viewed facilities and doctors near location:"
      );
    }
  }, [userLocation, userLoading]);

  const filteredFacilities = facilities.filter(
    (item) =>
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.address.toLowerCase().includes(search.toLowerCase())
  );

  const filteredDoctors = doctors.filter(
    (doc) =>
      doc.name.toLowerCase().includes(search.toLowerCase()) ||
      doc.facility.toLowerCase().includes(search.toLowerCase())
  );

  // Handler for telemedicine consultation
  const handleTelemedicine = async (doctor) => {
    // Jika dokter memiliki consultation_url khusus
    if (doctor.consultationUrl) {
      logUserActivity(
        "user",
        "telemedicine_consultation",
        "FaskesStroke",
        `User opened telemedicine consultation for doctor: ${doctor.name}`
      );
      window.open(doctor.consultationUrl, "_blank", "noopener,noreferrer");
    } else {
      // Gunakan platform telekonsultasi umum
      const message = `Konsultasi dengan ${doctor.name}\n\nAnda dapat menggunakan platform telekonsultasi berikut:\n\n• Halodoc: https://halodoc.com\n• Alodokter: https://alodokter.com\n• KlikDokter: https://klikdokter.com\n• ProSehat: https://prosehat.com\n\nCari nama dokter: "${doctor.name}"`;

      if (window.confirm(message + "\n\nApakah Anda ingin membuka Halodoc?")) {
        window.open("https://halodoc.com", "_blank", "noopener,noreferrer");
      }
    }
  };

  // Handler for showing doctor location on map
  const handleDoctorMap = async (doctor) => {
    if (doctor.location && doctor.location.lat && doctor.location.lng) {
      // Gunakan koordinat yang sudah ada
      logUserActivity(
        "user",
        "view_doctor_location",
        "FaskesStroke",
        `User viewed location for doctor: ${doctor.name}`
      );
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${doctor.location.lat},${doctor.location.lng}`;
      window.open(mapsUrl, "_blank", "noopener,noreferrer");
    } else if (doctor.facility && doctor.facility !== "-") {
      // Cari faskes berdasarkan nama menggunakan API
      try {
        const response = await api.get("/faskes/search", {
          params: { keyword: doctor.facility },
        });
        const faskes = response.data[0]; // Ambil hasil pertama
        if (faskes) {
          logUserActivity(
            "user",
            "view_doctor_location",
            "FaskesStroke",
            `User viewed location for doctor: ${doctor.name}`
          );
          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${faskes.lat},${faskes.lng}`;
          window.open(mapsUrl, "_blank", "noopener,noreferrer");
        } else {
          throw new Error("Faskes tidak ditemukan");
        }
      } catch (err) {
        const searchQuery = encodeURIComponent(doctor.facility);
        const mapsUrl = `https://www.google.com/maps/search/${searchQuery}`;
        window.open(mapsUrl, "_blank", "noopener,noreferrer");
      }
    } else {
      console.log("Lokasi dokter tidak tersedia:", doctor.name);
      console.log(doctor.facility);
      alert(
        "Lokasi dokter tidak tersedia. Silakan hubungi faskes terkait untuk informasi lokasi."
      );
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => {
            logUserActivity(
              "navigation",
              "go_back_from_faskes_stroke",
              "FaskesStroke",
              "User clicked back from FaskesStroke"
            );
            navigate("/");
          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Fasilitas Kesehatan</h1>
        <div className="h-6 w-6" /> {/* Placeholder */}
      </div>
      {locationError && (
        <div className="text-red-500 text-sm mb-2">{locationError}</div>
      )}

      {/* Tabs */}
      <div className="grid grid-cols-2 w-full border-b mb-4 text-sm">
        <button
          className={`pb-2 ${
            activeTab === "faskes"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("faskes")}
        >
          Faskes
        </button>
        <button
          className={`pb-2 ${
            activeTab === "dokter"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("dokter")}
        >
          Dokter
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder={
            activeTab === "faskes" ? "Cari faskes..." : "Cari dokter..."
          }
          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          Memuat data...
        </div>
      ) : activeTab === "faskes" ? (
        <div className="space-y-4">
          {filteredFacilities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {search
                ? "Tidak ada faskes yang ditemukan"
                : "Tidak ada data faskes tersedia"}
            </div>
          ) : (
            filteredFacilities.map((item, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-3"
              >
                <div className="flex justify-between items-center mb-1">
                  <p className="font-medium text-sm">{item.name}</p>
                  <p className="text-xs text-gray-500">
                    {item.distance && <>⚲ {item.distance}</>}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mb-3">{item.address}</p>

                <div className="flex gap-2 justify-between items-center">
                  <a
                    href={
                      "https://play.google.com/store/apps/details?id=app.bpjs.mobile&hl=id&pli=1"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    Buka di Mobile JKN
                  </a>

                  <div className="flex gap-2">
                    <a
                      href={item.maps_url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-24 border border-blue-500 text-blue-500 py-2 rounded-lg text-xs font-medium hover:bg-blue-50 transition text-center"
                    >
                      <span className="inline-flex items-center justify-center">
                        <Map size={14} className="mr-1" />
                        Lihat Map
                      </span>
                    </a>

                    {item.phone ? (
                      <a
                        href={`tel:${item.phone}`}
                        className="w-20 bg-blue-500 text-white py-2 rounded-lg text-xs font-medium hover:bg-blue-600 transition text-center"
                      >
                        <span className="inline-flex items-center justify-center">
                          <Phone size={14} className="mr-1" />
                          Hubungi
                        </span>
                      </a>
                    ) : (
                      <button
                        disabled
                        className="w-20 bg-gray-300 text-gray-500 py-2 rounded-lg text-xs font-medium cursor-not-allowed"
                        title="Nomor telepon tidak tersedia"
                      >
                        Hubungi
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDoctors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {search
                ? "Tidak ada dokter yang ditemukan"
                : "Tidak ada data dokter tersedia"}
            </div>
          ) : (
            filteredDoctors.map((doc, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-3"
              >
                <div className="flex justify-between items-center mb-1">
                  <p className="font-medium text-sm">{doc.name}</p>
                  <p className="text-xs text-gray-500">{doc.distance}</p>
                </div>
                <div className="text-xs text-gray-500 mb-1 space-y-1">
                  {doc.schedule.length > 0 ? (
                    doc.schedule.map((s, i) => <p key={i}>🕐 {s}</p>)
                  ) : (
                    <p className="text-gray-400">Jadwal tidak tersedia</p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-3">🏥 {doc.facility}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDoctorMap(doc)}
                    className="flex-1 border border-blue-500 text-blue-500 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 transition"
                    title={
                      doc.location
                        ? "Lihat lokasi di Google Maps"
                        : "Cari di Google Maps"
                    }
                  >
                    <span className="inline-flex items-center justify-center gap-1">
                      📍 Lihat Map
                    </span>
                  </button>
                  <button
                    onClick={() => handleTelemedicine(doc)}
                    className="flex-1 bg-blue-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition"
                    title={
                      doc.consultationUrl
                        ? "Buka telekonsultasi"
                        : "Lihat platform telekonsultasi"
                    }
                  >
                    <span className="inline-flex items-center justify-center gap-1">
                      💬 Telekonsultasi
                    </span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
